import React, { useEffect, useMemo, useState } from 'react';
import './reviews-table.css';
import GeneralAssessmentModal from './GeneralAssessmentModal';

const SECTIONS = [
    {
        key: 'positive',
        counter: 'GA 01',
        prompt: 'What went well and why?',
        employeeField: 'employee_comment_positive',
        managerField: 'manager_comment_positive',
    },
    {
        key: 'negative',
        counter: 'GA 02',
        prompt: 'What did not go well and why?',
        employeeField: 'employee_comment_negative',
        managerField: 'manager_comment_negative',
    },
];

/**
 * Props:
 *  - summary: { id, employee_comment_positive, manager_comment_positive, employee_comment_negative, manager_comment_negative }
 *  - role: "employee" | "manager"
 *  - onSaveComment: ({ id, field, value }) => Promise<void>
 */
export default function GeneralAssessment({
                                              summary = null,
                                              role = 'employee',
                                              onSaveComment,
                                          }) {
    const [local, setLocal] = useState(summary);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSection, setModalSection] = useState(null);
    const [modalRole, setModalRole] = useState(null);

    useEffect(() => {
        setLocal(summary);
    }, [summary?.id, summary]);

    const canEditEmployee = role === 'employee';
    const canEditManager = role === 'manager';
    const summaryId = local?.id ?? summary?.id ?? null;

    const rows = useMemo(() => {
        return SECTIONS.map((section) => {
            const employeeField = section.employeeField;
            const managerField = section.managerField;
            return {
                section,
                manager: {
                    label: 'Manager',
                    value: (local || summary)?.[managerField] ?? '',
                    canAct: canEditManager && !!summaryId,
                    field: managerField,
                },
                employee: {
                    label: 'Employee',
                    value: (local || summary)?.[employeeField] ?? '',
                    canAct: canEditEmployee && !!summaryId,
                    field: employeeField,
                },
            };
        });
    }, [local, summary, summaryId, canEditEmployee, canEditManager]);

    const openModal = (section, roleKey) => {
        setModalSection(section);
        setModalRole(roleKey);
        setModalOpen(true);
    };

    const handleSave = async (value) => {
        if (!summaryId || !modalSection) return;
        const field =
            modalRole === 'manager'
                ? modalSection.managerField
                : modalSection.employeeField;

        setLocal((prev) => ({ ...(prev || summary || {}), id: summaryId, [field]: value }));
        await onSaveComment?.({ id: summaryId, field, value });
        setModalOpen(false);
    };

    return (
        <div className="card">
            <div className="card-header">
                <h6 className="mb-0">General Assessment</h6>
            </div>
            <div className="card-body">
                <div className="reviews-table mt-4">
                    <table>
                        <thead>
                        <tr className="rt-head-top">
                            <th className="rt-counter">#</th>
                            <th className="rt-name">General Assessment Prompt</th>
                            <th className="rt-role" style={{ width: 120 }}>Role</th>
                            <th className="rt-comment-cell">Comment</th>
                            <th className="rt-actions" style={{ width: 120 }}>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {!summaryId && (
                            <tr>
                                <td colSpan={5} className="text-center text-muted py-3">
                                    General assessment not available for this review yet.
                                </td>
                            </tr>
                        )}

                        {summaryId &&
                            rows.map(({ section, manager, employee }, idx) => (
                                <React.Fragment key={section.key}>
                                    <tr className={`kpa-row ${idx % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-counter" rowSpan={2}>
                                            {section.counter}
                                        </td>
                                        <td className="rt-name" rowSpan={2}>
                                            {section.prompt}
                                        </td>
                                        <td className="rt-num">{manager.label}</td>
                                        <td className="rt-comment-cell">{manager.value || ''}</td>
                                        <td className="rt-actions">
                                            {manager.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={() => openModal(section, 'manager')}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                    <tr className={`kpa-row ${idx % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-num">{employee.label}</td>
                                        <td className="rt-comment-cell">{employee.value || ''}</td>
                                        <td className="rt-actions">
                                            {employee.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={() => openModal(section, 'employee')}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>

                    <GeneralAssessmentModal
                        show={modalOpen}
                        onClose={() => setModalOpen(false)}
                        section={modalSection}
                        roleLabel={modalRole === 'manager' ? 'Manager' : 'Employee'}
                        value={
                            modalSection && summaryId
                                ? (local || summary)?.[
                                modalRole === 'manager'
                                    ? modalSection.managerField
                                    : modalSection.employeeField
                                ] ?? ''
                                : ''
                        }
                        canEdit={
                            modalRole === 'manager' ? canEditManager && !!summaryId : canEditEmployee && !!summaryId
                        }
                        onSave={handleSave}
                    />
                </div>
            </div>
        </div>
    );
}
